import React, { useState, useEffect } from 'react'; // Ensure useEffect is imported
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { Download, ContentCopy } from '@mui/icons-material'; // Import icons
import { Info } from '@mui/icons-material'; // Import Info icon
import jsPDF from 'jspdf';
import CircularProgress from '@mui/material/CircularProgress'; // Import CircularProgress for spinner

// Utility function to handle API calls and progress simulation
const processTasks = async ({
  payload,
  setApiResult,
  setProgress,
  setShowSpinnerModal,
  setTabValue,
}) => {
  let interval; // Declare interval outside the try block

  try {
    setShowSpinnerModal(true); // Show spinner modal
    setProgress(0); // Reset progress

    // Start progress simulation
    interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(interval); // Stop the interval when progress reaches 100%
          return 100;
        }
        return prevProgress + 10; // Increment progress by 10%
      });
    }, 30000); // Update progress every 24 seconds (24000 ms)

    // Make the API call to /process-tasks/
    const response = await fetch('https://api.elevate.de.ltim.io/process-tasks/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    clearInterval(interval); // Stop progress simulation when the response is received

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Failed to process tasks:', errorData.detail);
      alert(`Error processing tasks: ${errorData.detail}`);
      setShowSpinnerModal(false); // Hide spinner modal
      return;
    }

    const data = await response.json();
    console.log('Tasks processed successfully:', JSON.stringify(data, null, 2)); // Print API result as a formatted string

    // Save the API result in the state
    setApiResult(
      typeof data.report === 'string'
        ? (() => {
            try {
              const parsedReport = JSON.parse(data.report); // Parse if it's a stringified JSON
              return {
                ...parsedReport,
                ddl: parsedReport.ddl ? parsedReport.ddl.replace(/\\n/g, '\n') : '',
                notes: parsedReport.notes
                  ? parsedReport.notes.map((note) => note.replace(/\\n/g, '\n'))
                  : [],
                links: data.links || [], // Include the links array
              };
            } catch (e) {
              return {
                report: data.report, // Use it as-is if parsing fails
                links: data.links || [], // Include the links array
              };
            }
          })()
        : {
            ...data.report,
            ddl: data.report.ddl ? data.report.ddl.replace(/\\n/g, '\n') : '',
            notes: data.report.notes
              ? data.report.notes.map((note) => note.replace(/\\n/g, '\n'))
              : [],
            links: data.links || [], // Include the links array
          }
    );

    setProgress(100); // Set progress to 100% on success
    setTimeout(() => {
      setShowSpinnerModal(false); // Hide spinner modal after 5000 ms
      setTabValue(1); // Automatically switch to the "Result" tab
    }, 5000); // Wait for 5000 ms before switching tabs
  } catch (error) {
    console.error('Error processing tasks:', error);
    alert(`Error processing tasks: ${error.message}`);
    clearInterval(interval); // Stop progress simulation
    setShowSpinnerModal(false); // Hide spinner modal
  }
};

const DbMigration = ({ }) => {
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [tabValue, setTabValue] = useState(0); // State for Tabs
  const [isFinalSubmit, setIsFinalSubmit] = useState(false); // State for final submit
  const [showSpinnerModal, setShowSpinnerModal] = useState(false); // State for spinner modal
  const [progress, setProgress] = useState(0); // State for progress percentage
  const [apiResult, setApiResult] = useState(null); // Add this state
  const [selectedMigrationType, setSelectedMigrationType] = useState(''); // State for migration type
  const [selectedDatabaseObject, setSelectedDatabaseObject] = useState([]); // State for selected database object
  const [migrationPurpose, setMigrationPurpose] = useState(''); // Add state for migration purpose
  const [codeConversionFileName, setCodeConversionFileName] = useState(''); // Add separate state for Code Conversion file
  const [testScriptFileName, setTestScriptFileName] = useState(''); // Add separate state for Test Script Generation file
  const [testDataFileName, setTestDataFileName] = useState(''); // Add separate state for Test Data Generation file
  const [documentationFileName, setDocumentationFileName] = useState(''); // Add separate state for Documentation file
  const [sourceDb, setSourceDb] = useState(''); // Local state for Source DB
  const [targetDb, setTargetDb] = useState(''); // Local state for Target DB
  const sourceOptions = ['MySQL', 'Microsoft SQL Server', 'Oracle', 'Amazon RDS', 'SQLite', 'MongoDB', 'IBM Db2'];
  const targetOptions = ['Postgresql',];
  const migrationOptions = ['DDL', 'SQL (Queries)', 'Stored Procedures (SP)', 'Triggers', 'Functions (UDFs)'];
  const dropdownStyle = { width: '300px' };
  const [specialInstruction, setSpecialInstruction] = useState(''); // Add this state
  const [selectedTestDataOption, setSelectedTestDataOption] = useState([]);

  const handleFileUpload = async (event, section) => {
    const file = event.target.files[0];
    if (file) {
      // Update the state based on the section
      if (section === 'codeConversion') {
        setCodeConversionFileName(file.name); // Update state for Code Conversion
      } else if (section === 'testScript') {
        setTestScriptFileName(file.name); // Update state for Test Script Generation
      } else if (section === 'testData') {
        setTestDataFileName(file.name); // Update state for Test Data Generation
      } else if (section === 'documentation') {
        setDocumentationFileName(file.name); // Update state for Documentation
      }

      // Upload the file to S3
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch('https://api.elevate.de.ltim.io/upload-to-s3/', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error(`Failed to upload ${file.name}:`, errorData.detail);
          alert(`Failed to upload ${file.name}: ${errorData.detail}`);
        } else {
          const data = await response.json();
          // console.log(`File uploaded successfully: ${data.filename}`);
          // alert(`File uploaded successfully: ${data.filename}`);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        alert(`Error uploading ${file.name}: ${error.message}`);
      }
    }
  };

  const handleSubmitTestScript = async () => {
    setIsFinalSubmit(true); // Set final submit state to true
    setShowSpinnerModal(true); // Show spinner modal
    setProgress(0); // Reset progress to 0

    // Prepare the payload for the API
    const payload = {
      source: sourceDb,
      target: targetDb,
      migration_type: "default",
      tasks: [{ task: "Test Script Generation", file_name: testScriptFileName }],
      is_test_script: true, // Set is_test_script to true
      is_test_data: false,
      instruction: "",
      is_documentation: false,
    };

    await processTasks({
      payload,
      setApiResult,
      setProgress,
      setShowSpinnerModal,
      setTabValue,
    });
  };

  const handleSubmitDocumentation = async () => {
    setIsFinalSubmit(true); // Set final submit state to true
    setShowSpinnerModal(true); // Show spinner modal
    setProgress(0); // Reset progress to 0

    // Prepare the payload for the API
    const payload = {
      source: sourceDb,
      target: targetDb,
      migration_type: "default",
      tasks: [{ task: "Documentation", file_name: documentationFileName }],
      is_test_script: false,
      is_test_data: false,
      instruction: "",
      is_documentation: true, // Set is_documentation to true
    };

    await processTasks({
      payload,
      setApiResult,
      setProgress,
      setShowSpinnerModal,
      setTabValue,
    });
  };

  const handleSubmitTestData = async () => {
    setIsFinalSubmit(true); // Set final submit state to true
    setShowSpinnerModal(true); // Show spinner modal
    setProgress(0); // Reset progress to 0

    // Prepare the payload for the API
    const payload = {
      source: sourceDb,
      target: targetDb,
      migration_type: "default",
      tasks: [{ task: "Test Data Generation", file_name: testDataFileName }],
      is_test_script: false,
      is_test_data: true, // Set is_test_data to true
      instruction: specialInstruction,
      is_documentation: false,
    };

    setSelectedTestDataOption(['testdata']);

    await processTasks({
      payload,
      setApiResult,
      setProgress,
      setShowSpinnerModal,
      setTabValue,
    });
  };

  const handleFinalSubmit = async () => {
    setIsFinalSubmit(true); // Set final submit state to true
    setShowSpinnerModal(true); // Show spinner modal
    setProgress(0); // Reset progress to 0

    // Prepare the payload for the API
    const tasks = selectedDatabaseObject.map((outputParam) => ({
      [outputParam]: codeConversionFileName, // Map output parameter to the uploaded file name
    }));

    const payload = {
      source: sourceDb, // Selected source dropdown value
      target: targetDb, // Selected target dropdown value
      migration_type: selectedMigrationType, // Selected migration type
      tasks, // Selected output parameters and file names
      is_test_script: selectedOption.includes('testdata'),
      is_test_data: selectedOption.includes('relevantTestData'),
      instruction: specialInstruction,
      is_documentation: selectedOption.includes('wantDocumentation'),
    };

    await processTasks({
      payload,
      setApiResult,
      setProgress,
      setShowSpinnerModal,
      setTabValue,
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDownloadPDF = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to download.');
      return;
    }

    // Create a new jsPDF instance
    const doc = new jsPDF();

    // Add a title to the PDF
    doc.setFontSize(16);
    doc.text('Output', 10, 10);

    // Extract the report content
    const resultContent = String(apiResult.report);

    // Add the content to the PDF with proper formatting
    doc.setFontSize(11);
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // Split the content into lines and handle page breaks
    const lines = doc.splitTextToSize(resultContent, contentWidth);
    let cursorY = 20;
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.height;

    lines.forEach((line) => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });

    // Save the PDF
    doc.save('Output.pdf');
  };

  const handleCopyCode = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to copy.');
      return;
    }

    // Extract the report content
    const resultContent = String(apiResult.report);

    // Copy the content to the clipboard
    navigator.clipboard.writeText(resultContent).then(() => {
      alert('Result content copied to clipboard!');
    }).catch((error) => {
      console.error('Failed to copy text:', error);
    });
  };

  const handleDownloadTestData = () => {
    // Check if apiResult is null or undefined
    console.log(apiResult);
    if (!apiResult) {
      console.error('API result is not available.');
      alert('No API result available.');
      return;
    }

    // Check if links property exists and is an array
    if (!Array.isArray(apiResult.links) || apiResult.links.length === 0) {
      console.error('No test data links available in the API result.');
      alert('No test data links available to download.');
      return;
    }

    // Recursive function to download files sequentially
    const downloadFile = (index) => {
      if (index >= apiResult.links.length) {
        return; // Stop when all files are downloaded
      }

      const link = apiResult.links[index];
      if (typeof link === 'string' && link.trim() !== '') {
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.target = '_self'; // Ensure the file is downloaded directly
        anchor.download = ''; // Let the browser handle the filename
        document.body.appendChild(anchor); // Append the anchor to the DOM
        anchor.click(); // Trigger the download
        document.body.removeChild(anchor); // Remove the anchor after the download

        // Call the next download after a delay
        setTimeout(() => downloadFile(index + 1), 1000); // Delay of 1 second between downloads
      } else {
        console.error('Invalid link:', link);
        downloadFile(index + 1); // Skip invalid links and move to the next
      }
    };

    // Start downloading from the first file
    downloadFile(0);
  };

  // Function to reset all migration data
  const resetAllMigrationData = () => {
    // Reset Code Conversion states
    setCodeConversionFileName('');
    setSelectedDatabaseObject([]);
    setSelectedOption([]);
    setSourceSearch('');
    setTargetSearch('');
    setSourceDb('');
    setTargetDb('');
    setSelectedMigrationType('');

    // Reset Test Script Generation states
    setTestScriptFileName('');

    // Reset Test Data Generation states
    setTestDataFileName('');
    setSelectedTestDataOption([]);

    // Reset Documentation states
    setDocumentationFileName('');

    // Reset common states
    setShowSummary(false);
    setSpecialInstruction('');
    setApiResult(null);
    setIsFinalSubmit(false);
    setProgress(0);
  };

  // Use useEffect to reset data when migrationPurpose changes
  useEffect(() => {
    resetAllMigrationData();
  }, [migrationPurpose]);

  // Reset selectedDatabaseObject when selectedMigrationType changes
  useEffect(() => {
    setSelectedDatabaseObject([]); // Clear the database object dropdown value
  }, [selectedMigrationType]);

  // Dynamically filter databaseObjectOptions based on selectedMigrationType
  const getFilteredDatabaseObjectOptions = () => {
    switch (selectedMigrationType) {
      case 'DDL':
        return ['DDL'];
      case 'SQL (Queries)':
        return ['SQL', 'Stored Procedure', 'Python'];
      case 'Stored Procedures (SP)':
        return ['Stored Procedure', 'Python'];
      case 'Triggers':
        return ['SQL', 'Python'];
      case 'Functions (UDFs)':
        return ['SQL', 'Python'];
      default:
        return [];
    }
  };

  return (
    <Box sx={{ width: '100%', marginTop: -5 }}>
      {/* Tabs for Input and Result */}
      <Tabs value={tabValue} onChange={handleTabChange} sx={{
        '& .MuiTab-root': {
          fontWeight: 'bold', // Make the tab headings bold
        },
      }}>
        <Tab label="Input" />
        <Tab label="Result" />
      </Tabs>

      {/* Input Tab Content */}
      {tabValue === 0 && (
        <Box sx={{ marginTop: 2 }}>
          {/* Purpose of Migration Section */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
              What is the purpose of migration?
            </Typography>
            <RadioGroup
              row
              value={migrationPurpose}
              onChange={(event) => setMigrationPurpose(event.target.value)} // Update state on selection
            >
              <FormControlLabel value="Code Conversion" control={<Radio />} label="Code Conversion" />
              <FormControlLabel value="Test Script Generation" control={<Radio />} label="Test Script Generation" />
              <FormControlLabel value="Test Data Generation" control={<Radio />} label="Test Data Generation" />
              <FormControlLabel value="Documentation" control={<Radio />} label="Documentation" />
            </RadioGroup>
          </Box>

          {/* Conditionally render content based on migration purpose */}
          {migrationPurpose === 'Code Conversion' && (
            <Box
              className="dropdownContainer"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {/* Input Section */}
              <Box
                className="dropdownContainer"
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'flex-start', // Align items to the top
                  justifyContent: 'space-between', // Space out items horizontally
                  width: '100%',
                }}
              >
                {/* Source and Migration Type Dropdowns */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column', // Stack Source and Migration Type vertically
                    gap: 2, // Space between dropdowns
                    flex: 1, // Allow this container to take up available space
                  }}
                >
                  {/* Source Dropdown */}
                  <Box sx={{ maxWidth: '300px' }}>
                    <Typography variant="subtitle1" id="source-label" sx={{ fontWeight: 'bold' }}>
                      Source<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={sourceOptions}
                      value={sourceDb || ''} // Use local state for Source DB
                      onChange={(event, newValue) => {
                        setSourceDb(newValue || '');
                      }}
                      inputValue={sourceSearch}
                      onInputChange={(event, newInputValue) => setSourceSearch(newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Source"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      sx={dropdownStyle}
                    />
                  </Box>

                  {/* Migration Type Dropdown */}
                  <Box sx={{ maxWidth: '300px' }}>
                    <Typography variant="subtitle1" id="migration-type-label" sx={{ fontWeight: 'bold' }}>
                      What do you want to migrate?<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={migrationOptions}
                      value={selectedMigrationType || ''}
                      onChange={(event, newValue) => {
                        setSelectedMigrationType(newValue || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Migration Type"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      sx={dropdownStyle}
                    />
                  </Box>
                  {/* Database Object Dropdown */}
                  {codeConversionFileName && (
                    <Box sx={{ maxWidth: '300px', whiteSpace: 'nowrap' }}>
                      <Typography variant="subtitle1" id="database-object-label" sx={{ fontWeight: 'bold' }}>
                        Please select the Database Object you need to convert for migration<span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Autocomplete
                        multiple // Enable multi-select
                        options={getFilteredDatabaseObjectOptions()} // Use the filtered options
                        value={selectedDatabaseObject || []} // Ensure it's an array for multi-select
                        onChange={(event, newValue) => {
                          setSelectedDatabaseObject(newValue || []); // Update state with selected values
                        }}
                        disableCloseOnSelect // Keep the dropdown open after selecting a value
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={selectedDatabaseObject.length === 0 ? "Select Database Objects" : ""} // Show placeholder only if no value is selected
                            variant="outlined"
                            size="small"
                          />
                        )}
                        sx={dropdownStyle}
                      />
                    </Box>
                  )}

                  {/* Message and Radio Buttons */}
                  {selectedDatabaseObject.length > 0 && (
                    <Box sx={{ marginTop: 2 }}>
                      {/* Test Script Message */}
                      <Box sx={{ marginBottom: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                          Do you want to generate the test script?
                        </Typography>
                        <RadioGroup
                          row
                          value={selectedOption.includes('testdata') ? 'Yes' : selectedOption.includes('notestdata') ? 'No' : ''} // No default selection
                          onChange={(event) => {
                            if (event.target.value === 'Yes') {
                              setSelectedOption(['testdata']); // Set 'testdata' as the selected option
                            } else if (event.target.value === 'No') {
                              setSelectedOption((prev) => [...prev.filter(option => option !== 'testdata'), 'notestdata']); // Add 'notestdata' and remove 'testdata'
                            }
                            setShowSummary(true); // Show the Selected Options box immediately
                          }}
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </Box>


                      <Box sx={{ marginTop: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                          Documentation Required?
                        </Typography>
                        <RadioGroup
                          row
                          value={selectedOption.includes('wantDocumentation') ? 'Yes' : selectedOption.includes('noDocumentation') ? 'No' : ''} // No default selection
                          onChange={(event) => {
                            if (event.target.value === 'Yes') {
                              setSelectedOption([...selectedOption.filter(option => option !== 'noDocumentation'), 'wantDocumentation']); // Add 'wantDocumentation' and remove 'noDocumentation'
                            } else if (event.target.value === 'No') {
                              setSelectedOption([...selectedOption.filter(option => option !== 'wantDocumentation'), 'noDocumentation']); // Add 'noDocumentation' and remove 'wantDocumentation'
                            }
                          }}
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </Box>

                    </Box>
                  )}

                  {/* Selected Options Box */}
                  {(selectedOption.includes('notestdata') || selectedOption.includes('wantDocumentation') || selectedOption.includes('noDocumentation')) && (
                    <Box
                      sx={{
                        marginTop: 2,
                        padding: 2,
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Typography variant="h6" sx={{ marginBottom: 2 , fontWeight: 'bold' }}>
                        Selected Options and Uploaded Files
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body1">
                          <strong>Database Object:</strong>{' '}
                          {selectedDatabaseObject.length > 0
                            ? selectedDatabaseObject.join(' ') // Join values with a space
                            : 'Not selected'}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Uploaded File:</strong> {codeConversionFileName || 'No file uploaded'}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Generate Result Button */}
                  {(selectedOption.includes('notestdata') || selectedOption.includes('wantDocumentation') || selectedOption.includes('noDocumentation')) && (
                    <Box sx={{ marginTop: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleFinalSubmit} // Keep the same handler
                        sx={{
                          backgroundColor: 'white',
                          color: 'black',
                          border: '1px solid black',
                          boxShadow: 'none',
                        }}
                      >
                        Generate Result
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Target Dropdown and Upload Button */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column', // Stack Target Dropdown, Upload Button, and Relevant Test Data vertically
                    gap: 2, // Space between elements
                    flex: 1,
                    maxWidth: '300px',
                    marginRight: '150px',
                  }}
                >
                  {/* Target Dropdown */}
                  <Box>
                    <Typography variant="subtitle1" id="target-label" sx={{ fontWeight: 'bold' }}>
                      Target<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <Autocomplete
                      options={targetOptions}
                      value={targetDb || ''} // Use local state for Target DB
                      onChange={(event, newValue) => {
                        setTargetDb(newValue || '');
                      }}
                      inputValue={targetSearch}
                      onInputChange={(event, newInputValue) => setTargetSearch(newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Target"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      sx={dropdownStyle}
                    />
                  </Box>

                  {/* Upload Data Button */}
                  <Box>
                    {sourceDb && targetDb && selectedMigrationType && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          component="label"
                          sx={{
                            backgroundColor: 'white',
                            color: 'black',
                            border: '1px solid black',
                            boxShadow: 'none',
                            height: '40px',
                            marginTop: '20px',
                          }}
                        >
                          Upload Data
                          <input
                            type="file"
                            hidden
                            onChange={(event) => handleFileUpload(event, 'codeConversion')} // Call the file upload handler for Code Conversion
                          />
                        </Button>
                        {/* Display the uploaded file name */}
                        {codeConversionFileName && (
                          <Typography
                            variant="body2"
                            sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
                          >
                            Uploaded File: {codeConversionFileName}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Relevant Test Data Message */}
                  {selectedOption.includes('testdata') && ( // Render only if "Yes" is selected for test script
                    <Box
                      sx={{
                        maxWidth: '300px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginTop: 11, // Add spacing from the Upload Button
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                        Do you want to generate the relevant test data also?
                      </Typography>
                      <RadioGroup
                        row
                        value={selectedOption.includes('relevantTestData') ? 'Yes' : selectedOption.includes('noRelevantTestData') ? 'No' : ''} // No default selection
                        onChange={(event) => {
                          if (event.target.value === 'Yes') {
                            setSelectedOption([...selectedOption, 'relevantTestData']); // Add 'relevantTestData' to the selected options
                          } else if (event.target.value === 'No') {
                            setSelectedOption(selectedOption.filter(option => option !== 'relevantTestData')); // Remove 'relevantTestData' from the selected options
                            setSelectedOption([...selectedOption, 'noRelevantTestData']); // Add 'noRelevantTestData' to the selected options
                          }
                        }}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>

                      {/* Multiline Textbox for Special Instructions */}
                      {selectedOption.includes('relevantTestData') && (
                        <Box sx={{ marginTop: 2, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', marginRight: 1 }}>
                              Special Instruction for Test Data
                            </Typography>
                            {/* Tooltip with Info Icon */}
                            <Tooltip title="Generate 50 rows of data" arrow>
                              <Info sx={{ color: 'gray', cursor: 'pointer' }} />
                            </Tooltip>
                          </Box>
                          <TextField
                            multiline
                            variant="outlined"
                            fullWidth
                            value={specialInstruction} // Bind the state to the textbox
                            onChange={(event) => {
                              const value = event.target.value;
                              setSpecialInstruction(value); // Update the state with the textbox value
                              if (value.trim() !== '') {
                                setSelectedOption([...selectedOption, 'specialInstructionGiven']); // Add 'specialInstructionGiven' to the selected options
                              } else {
                                setSelectedOption(selectedOption.filter(option => option !== 'specialInstructionGiven')); // Remove 'specialInstructionGiven' if the input is cleared
                              }
                            }}
                          />
                        </Box>
                      )}


                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {migrationPurpose === 'Test Script Generation' && (
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Please upload file for test script generation
              </Typography>
              <Button
                variant="contained"
                color="primary"
                component="label"
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={(event) => handleFileUpload(event, 'testScript')} // Use the file upload handler for Test Script Generation
                />
              </Button>
              {/* Display the uploaded file name */}
              {testScriptFileName && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
                  >
                    Uploaded File: {testScriptFileName}
                  </Typography>
                  {/* Submit button visible only after file upload */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitTestScript}
                    sx={{
                      marginTop: 2,
                      backgroundColor: 'white',
                      color: 'black',
                      border: '1px solid black',
                      boxShadow: 'none',
                      height: '40px',
                    }}
                  >
                    Submit
                  </Button>
                </>
              )}
            </Box>
          )}

          {migrationPurpose === 'Test Data Generation' && (
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Please upload file for test data generation
              </Typography>
              <Button
                variant="contained"
                color="primary"
                component="label"
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={(event) => handleFileUpload(event, 'testData')} // Use the file upload handler for Test Data Generation
                />
              </Button>
              {/* Display the uploaded file name */}
              {testDataFileName && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
                  >
                    Uploaded File: {testDataFileName}
                  </Typography>
                  {/* Special Instruction TextField with Tooltip */}
                  <Box sx={{ marginTop: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', marginRight: 1 }}>
                        Special Instruction for Test Data
                      </Typography>
                      <Tooltip title="Generate 50 rows of data" arrow>
                        <Info sx={{ color: 'gray', cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={specialInstruction}
                      onChange={(event) => setSpecialInstruction(event.target.value)}
                    />
                  </Box>
                  {/* Submit button visible only after file upload */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitTestData}
                    sx={{
                      marginTop: 2,
                      backgroundColor: 'white',
                      color: 'black',
                      border: '1px solid black',
                      boxShadow: 'none',
                      height: '40px',
                    }}
                  >
                    Submit
                  </Button>
                </>
              )}
            </Box>
          )}

          {migrationPurpose === 'Documentation' && (
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Please upload a file for Documentation
              </Typography>
              <Button
                variant="contained"
                color="primary"
                component="label"
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={(event) => handleFileUpload(event, 'documentation')} // Use the file upload handler for Documentation
                />
              </Button>
              {/* Display the uploaded file name */}
              {documentationFileName && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
                  >
                    Uploaded File: {documentationFileName}
                  </Typography>
                  {/* Submit button visible only after file upload */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitDocumentation}
                    sx={{
                      marginTop: 2,
                      backgroundColor: 'white',
                      color: 'black',
                      border: '1px solid black',
                      boxShadow: 'none',
                      height: '40px',
                    }}
                  >
                    Submit
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Result Tab Content */}
      {tabValue === 1 && isFinalSubmit && (
        <>
          <Box
            id="result-content" // Add this ID
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Output
            </Typography>
            {apiResult ? (
              <>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                >
                  {String(apiResult.report)}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}
          </Box>

          {/* Add this message for Test Data */}
          {selectedOption.includes('testdata') && (
            <Typography
              variant="body1"
              sx={{
                marginTop: 2,
                color: 'green',
                fontWeight: 'bold',
              }}
            >
              Test data has been generated successfully.
            </Typography>
          )}

          {/* Buttons with Icons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
              marginTop: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<Download />}
            >
              Download PDF
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyCode}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<ContentCopy />}
            >
              Copy Code
            </Button>

            {/* Download Test Data Button */}
            {(selectedOption.includes('testdata') || selectedTestDataOption.includes('testdata')) && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownloadTestData}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
                startIcon={<Download />}
              >
                Download Test Data
              </Button>
            )}
          </Box>
        </>
      )}

      {/* Spinner Modal */}
      <Dialog open={showSpinnerModal} onClose={() => setShowSpinnerModal(false)}>
        <DialogContent sx={{ textAlign: 'center', padding: 4 }}>
          {progress < 100 ? (
            <>
              <CircularProgress />
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Document is being generated...
              </Typography>
              <Typography variant="body1" sx={{ marginTop: 1 }}>
                {progress}%
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'green',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h4" sx={{ color: 'white' }}>✔</Typography>
              </Box>
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Document Generated Successfully!
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DbMigration;